import { useId, useMemo } from "react";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { canManageScheduleMasterData } from "@/features/auth/permissions";
import { useScheduleIssues } from "../api/use-issues";
import { useLatestRun, useRun } from "../api/use-runs";
import { useSchedule } from "../api/use-schedules";
import { computeSectionRoster } from "../lib/compute-section-roster";
import { formatEntityLabel } from "../lib/format-entity-label";
import { acceptedNormalizedCourseIds as acceptedCodesFor } from "../lib/accepted-course-ids";
import type { ReassignHandler } from "./reassign-handler";
import type {
  Assignment,
  CalendarEvent,
  ScheduleValidationIssue,
} from "../types";
import { SidePanel } from "./side-panel";

interface EventDetailPanelProps {
  scheduleId: string;
  event: CalendarEvent;
  onClose: () => void;
  /**
   * Invoked when a roster row's "Reassign" link is activated. Slice 4 wires
   * this through to the Reassign modal; in Slice 3 the parent supplies a
   * stub (e.g. `console.warn`).
   */
  onReassign: ReassignHandler;
  /**
   * Forwarded to the underlying {@link SidePanel}. Set by the page when a
   * successor surface (Reassign modal) is about to mount so the panel's own
   * focus-restore-on-close doesn't steal focus from the modal.
   */
  skipFocusRestore?: boolean;
}

/**
 * Right-side drawer triggered from a calendar event tile. Surfaces the
 * event's section metadata, any PostRun validation issues tied to that
 * section, and the current roster (computed from the latest run's
 * assignments).
 */
export function EventDetailPanel({
  scheduleId,
  event,
  onClose,
  onReassign,
  skipFocusRestore = false,
}: EventDetailPanelProps) {
  const titleId = useId();
  const { user } = useAuth();
  const canReassign = canManageScheduleMasterData(user?.role);

  const scheduleQuery = useSchedule(scheduleId);
  const timeZone = scheduleQuery.data?.timeZone ?? "UTC";

  const latestRun = useLatestRun(scheduleId);
  const runQuery = useRun(scheduleId, latestRun.data?.id ?? "");
  // `Run` only loads once `useLatestRun` has surfaced a run id; the
  // `enabled` flag inside `useRun` keeps the fetch off the wire otherwise.
  const run = runQuery.data;

  const issuesQuery = useScheduleIssues(scheduleId);
  const sectionKey = event.section ?? "";

  // Display forms of every code this one event serves: the primary courseId
  // plus any additional codes from a multi-value `Course ID:` line. Drives the
  // header so it reads "A / B" — consistent with the sections table and the
  // section roster panel, and sourced from the SAME field the roster below
  // aggregates on (additionalCourseIds), not from linkedCourses (co-reqs).
  const acceptedDisplayCourseIds = useMemo<string[]>(() => {
    const codes = event.courseId ? [event.courseId.trim()] : [];
    for (const raw of event.additionalCourseIds?.split(",") ?? []) {
      const t = raw.trim();
      if (t) codes.push(t);
    }
    return codes;
  }, [event.courseId, event.additionalCourseIds]);

  // Normalized + deduped forms for roster matching. Assignments carry the
  // student's requested code, so the roster matches on membership in this set.
  // Shared with the calendar course-highlight via `acceptedNormalizedCourseIds`.
  const acceptedNormalizedCourseIds = useMemo<string[]>(
    // Depend on the whole `event` — the React Compiler lint requires the dep to
    // match what the body reads (`acceptedCodesFor(event)`). In practice `event`
    // is a stable prop from `panelContext`, so this rarely recomputes.
    () => acceptedCodesFor(event),
    [event],
  );

  // Validation issues attached to THIS section. PostRun issues only — Stage
  // 1/2 issues don't carry course/section context.
  const sectionIssues = useMemo<ScheduleValidationIssue[]>(() => {
    if (!issuesQuery.data) return [];
    return issuesQuery.data.filter(
      (i) =>
        i.stage === "PostRun" &&
        i.courseId === event.courseId &&
        (i.sectionLabel ?? "") === sectionKey,
    );
  }, [issuesQuery.data, event.courseId, sectionKey]);

  // Per-student flag badge: surfaces the leftmost PostRun issue tied to this
  // (student, course, section) tuple. Drives the small "Conflict" / "Capacity"
  // chip beside each roster row.
  const studentIssueFlags = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const issue of sectionIssues) {
      const sid = issue.normalizedStudentIdentifier;
      if (!sid) continue;
      if (map.has(sid)) continue;
      // Code shape is dotted, e.g. `Scheduler.Student.TimeConflict` or
      // `Scheduler.Section.CapacityExceeded`. Use the leaf segment as the
      // short label on the roster row chip.
      const parts = issue.code.split(".");
      const label = parts[parts.length - 1] ?? issue.code;
      map.set(sid, label);
    }
    return map;
  }, [sectionIssues]);

  const roster = useMemo<Assignment[]>(
    () => computeSectionRoster(run, acceptedNormalizedCourseIds, event.section),
    [run, acceptedNormalizedCourseIds, event.section],
  );

  const showRosterPlaceholder = !latestRun.data || runQuery.isLoading;

  const timeLabel = formatEventTime(event, timeZone);
  const sectionLabel = event.section ?? "—";

  return (
    <SidePanel
      onClose={onClose}
      ariaLabelledBy={titleId}
      testId="event-detail-panel"
      skipFocusRestore={skipFocusRestore}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 py-[18px] border-b border-border-subtle">
        <div className="min-w-0">
          <div
            data-testid="event-detail-course-id"
            className="font-mono text-[13px] font-semibold text-blue-300 flex flex-wrap items-center gap-1.5"
          >
            <span>
              {acceptedDisplayCourseIds.length > 0
                ? acceptedDisplayCourseIds.join(" / ")
                : "—"}
            </span>
            {event.linkedCourses &&
              event.linkedCourses
                .split(",")
                .map((c) => c.trim())
                .filter(Boolean)
                .map((c) => (
                  <span
                    key={c}
                    data-testid={`event-detail-linked-course-${c}`}
                    className="text-[11px] font-mono font-medium text-blue-200/80 bg-blue-500/10 border border-blue-500/30 rounded px-1.5 py-0.5"
                  >
                    {c}
                  </span>
                ))}
          </div>
          <h3
            id={titleId}
            data-testid="event-detail-title"
            className="mt-1 text-[17px] font-semibold text-white"
          >
            {event.summary}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          data-testid="event-detail-close"
          className="text-text-muted hover:text-white text-[20px] leading-none cursor-pointer"
        >
          ×
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-[18px]">
        <div className="grid grid-cols-2 gap-3 mb-[18px]">
          <MetaCell label="Teacher" value={event.teacherEmail ?? "—"} />
          <MetaCell label="Room" value={event.location ?? "—"} />
          <MetaCell label="Time" value={timeLabel} />
          <MetaCell label="Section" value={sectionLabel} />
        </div>

        {event.rawDescription && (
          <details
            data-testid="event-detail-description"
            className="bg-page border border-border-subtle rounded-md mb-[18px] overflow-hidden"
          >
            <summary className="cursor-pointer px-3.5 py-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted hover:text-text-soft select-none">
              Description
            </summary>
            <pre
              data-testid="event-detail-description-body"
              className="px-3.5 py-2 text-[12px] font-mono text-text-soft whitespace-pre-wrap break-words max-h-64 overflow-y-auto border-t border-border-subtle"
            >
              {event.rawDescription}
            </pre>
          </details>
        )}

        {sectionIssues.length > 0 && (
          <div
            data-testid="event-detail-issues"
            className="bg-page border border-amber-900/60 rounded-md px-3.5 py-3 mb-[18px]"
          >
            <div className="flex items-center gap-2 mb-2.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fbbf24"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                <path d="M12 9v4M12 17h.01" />
              </svg>
              <span className="text-[12px] font-semibold uppercase tracking-wider text-amber-200">
                Validation issues
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {sectionIssues.map((issue) => (
                <div
                  key={issue.id}
                  data-testid={`event-detail-issue-${issue.id}`}
                  className="flex items-start gap-2"
                >
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold text-text-soft">
                      {issue.code}
                    </div>
                    <div className="text-[12px] text-text-muted leading-snug">
                      {issue.message}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[12px] font-semibold uppercase tracking-wider text-text-muted">
            Assigned roster
          </span>
          <span className="text-[12px] text-text-muted">
            {roster.length}
            {event.capacity != null ? `/${event.capacity}` : ""} assigned
          </span>
        </div>

        {showRosterPlaceholder ? (
          <div
            data-testid="event-detail-no-run"
            className="bg-page border border-border-subtle rounded-md p-4 text-[13px] text-text-muted"
          >
            No run available — run the scheduler to see rosters.
          </div>
        ) : roster.length === 0 ? (
          <div
            data-testid="event-detail-empty-roster"
            className="bg-page border border-border-subtle rounded-md p-4 text-[13px] text-text-muted"
          >
            No students assigned to this section.
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {roster.map((assignment) => (
              <RosterRow
                key={assignment.id}
                assignment={assignment}
                flag={
                  studentIssueFlags.get(
                    assignment.normalizedStudentIdentifier,
                  ) ?? null
                }
                canReassign={canReassign}
                onReassign={() =>
                  onReassign(
                    assignment.studentIdentifier,
                    event.courseId ?? "",
                    event.section,
                    assignment.studentName,
                    event.summary,
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
    </SidePanel>
  );
}

interface MetaCellProps {
  label: string;
  value: string;
}

function MetaCell({ label, value }: MetaCellProps) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-text-faint mb-[3px]">
        {label}
      </div>
      <div className="text-[13px] text-text-soft">{value}</div>
    </div>
  );
}

interface RosterRowProps {
  assignment: Assignment;
  flag: string | null;
  canReassign: boolean;
  onReassign: () => void;
}

function RosterRow({
  assignment,
  flag,
  canReassign,
  onReassign,
}: RosterRowProps) {
  const initials = computeInitials(assignment.studentIdentifier);
  return (
    <div
      data-testid={`event-detail-roster-row-${assignment.id}`}
      className="flex items-center justify-between bg-page border border-border-subtle rounded-md px-3 py-2"
    >
      <div className="flex items-center gap-2.5">
        <div
          aria-hidden="true"
          className="w-6 h-6 rounded-full bg-surface-2 flex items-center justify-center text-[10px] font-semibold text-text-soft"
        >
          {initials}
        </div>
        <span className="text-[13px] text-text-soft">
          {formatEntityLabel(
            assignment.studentName,
            assignment.studentIdentifier,
          )}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {flag && (
          <span
            data-testid={`event-detail-flag-${assignment.id}`}
            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300"
          >
            {flag}
          </span>
        )}
        {canReassign && (
          <button
            type="button"
            onClick={onReassign}
            data-testid={`event-detail-reassign-${assignment.id}`}
            className="text-[12px] text-blue-400 hover:text-blue-300 cursor-pointer"
          >
            Reassign
          </button>
        )}
      </div>
    </div>
  );
}

/** Two-character initials from a free-form identifier. */
function computeInitials(identifier: string): string {
  const cleaned = identifier.trim();
  if (cleaned.length === 0) return "??";
  const parts = cleaned.split(/[\s_-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
}

/**
 * Formats a CalendarEvent's start/end in the schedule's timezone using the
 * same lowercase am/pm style the calendar grid tiles use (e.g. `10:40am –
 * 1:40pm`) so the sidebar and tile don't disagree stylistically. Returns
 * `"—"` if the timestamps are missing or unparseable.
 */
function formatEventTime(event: CalendarEvent, timeZone: string): string {
  if (!event.dtStartUtc || !event.dtEndUtc) return "—";
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone,
    });
    const start = normalize(fmt.format(new Date(event.dtStartUtc)));
    const end = normalize(fmt.format(new Date(event.dtEndUtc)));
    return `${start} – ${end}`;
  } catch {
    return "—";
  }
}

/**
 * Intl.DateTimeFormat emits "10:40 AM" (uppercase, space-separated); the
 * grid tiles emit "10:40am". Match the tile style so the two surfaces read
 * consistently.
 */
function normalize(rendered: string): string {
  return rendered.replace(/\s+(AM|PM)$/i, (_, ap: string) => ap.toLowerCase());
}
